// src/utils/railOptimisation.js

// Constants
const ENTRAXE_STANDARD = 60; // cm
const LARGEUR_RAIL = 4.8; // cm (standard R48/M48 width depth, but strictly for visual plan we often draw them as lines or 4-5cm boxes)
// For calculation of quantity, we focus on linear length.
// For visualization, we might want a small width.

/**
 * Checks if a value is strictly between two others
 */
const isBetween = (val, min, max) => val > min && val < max;

// Seuil (cm) au-dessus duquel une ouverture "autre" est considérée comme structurelle.
const SEUIL_OUVERTURE_STRUCTURELLE = 40;

/**
 * Une ouverture est "structurelle" si elle impose un cadre dans l'ossature
 * (montants d'encadrement, linteau/appui) : portes et fenêtres, ou toute grande
 * ouverture. Les prises, interrupteurs et tuyaux sont de simples découpes dans
 * la plaque : ils ne doivent PAS ajouter de montants ni interrompre les rails.
 */
const estStructurelle = (o) => {
    if (o.type === 'porte' || o.type === 'fenêtre') return true;
    if (o.type === 'prise' || o.type === 'interrupteur' || o.type === "tuyau d'eau") return false;
    // Repli par taille pour le type "autre" ou les ouvertures sans type connu
    return o.largeur >= SEUIL_OUVERTURE_STRUCTURELLE && o.hauteur >= SEUIL_OUVERTURE_STRUCTURELLE;
};

/**
 * Calculates rails (horizontal) and montants (vertical) for a wall.
 * 
 * @param {Object} mur - { largeur, hauteur, ouvertures: [] }
 * @returns {Object} { elements: [], resume: {} }
 */
export const calculerOssature = (mur) => {
    const elements = [];
    const stats = {
        railsHaut: 0, // count
        railsBas: 0,
        montants: 0, // count of full height equivalent? Or just distinct pieces?
        longueurTotaleRails: 0, // cm
        longueurTotaleMontants: 0 // cm
    };

    const { largeur, hauteur, ouvertures } = mur;

    // --- Cas Spécifique : Plafond ---
    if (mur.type === 'plafond') {
        // Pour un plafond, on utilise des fourrures (F530)
        // Entraxe standard plafond : 50 cm
        const ENTRAXE_PLAFOND = 50;

        // 1. Cornières de rive (Périphérie)
        // On met des rails sur les 4 côtés (simplification visuelle)

        // Bas (0)
        elements.push({
            type: 'corniere', // ou 'rail'
            x: 0,
            y: 0,
            longueur: largeur,
            orientation: 'horizontal' // Visuellement sur l'axe X
        });

        // Haut (hauteur)
        elements.push({
            type: 'corniere',
            x: 0,
            y: hauteur - 0.1,
            longueur: largeur,
            orientation: 'horizontal'
        });

        // Gauche (0)
        elements.push({
            type: 'corniere',
            x: 0,
            y: 0,
            longueur: hauteur,
            orientation: 'vertical' // Visuellement sur l'axe Y
        });

        // Droite (largeur)
        elements.push({
            type: 'corniere',
            x: largeur - 0.1,
            y: 0,
            longueur: hauteur,
            orientation: 'vertical'
        });

        stats.railsHaut += 2; // Cornières horizontales
        stats.montants += 2; // Cornières verticales (abus de langage pour stats)
        stats.longueurTotaleRails += (largeur * 2 + hauteur * 2);

        // 2. Fourrures (Dans le sens de la largeur ou hauteur ?)
        // Généralement perpendiculaire aux plaques.
        // Si les plaques sont posées en longueur (axe X), fourrures en Y.
        // On va dire fourrures verticales (axe Y) espacées de 50cm sur l'axe X.

        for (let x = ENTRAXE_PLAFOND; x < largeur; x += ENTRAXE_PLAFOND) {
            elements.push({
                type: 'fourrure',
                x: x,
                y: 0, // Du bas
                longueur: hauteur, // Jusqu'en haut
                orientation: 'vertical'
            });
            stats.longueurTotaleMontants += hauteur; // On compte en "montant" pour l'instant
            stats.montants++;
        }

        // TODO: Ajouter les suspentes sur les fourrures

        return { elements, stats };
    }

    // --- 1. Rails Horizontaux (Tracks) ---

    // A. Rail Haut (Plafond)
    // Generally continuous unless complex ceiling structure, assuming simple wall here.
    elements.push({
        type: 'rail',
        position: 'haut',
        x: 0,
        y: hauteur - 0.1, // Visual offset
        longueur: largeur,
        orientation: 'horizontal'
    });
    stats.longueurTotaleRails += largeur;
    stats.railsHaut++;

    // B. Rail Bas (Sol)
    // Interrupted by doors (openings with y=0)
    let segmentsSol = [{ start: 0, end: largeur }];

    // Filter openings that touch the floor (portes structurelles uniquement).
    // Un tuyau ou une prise au niveau du sol ne doit pas couper le rail bas.
    const portes = ouvertures.filter(o => o.y <= 0.1 && estStructurelle(o));

    portes.forEach(porte => {
        const portStart = porte.x;
        const portEnd = porte.x + porte.largeur;

        const nouveauxSegments = [];
        segmentsSol.forEach(seg => {
            if (seg.end <= portStart || seg.start >= portEnd) {
                // No overlap
                nouveauxSegments.push(seg);
            } else {
                // Overlap, cut segment
                if (seg.start < portStart) {
                    nouveauxSegments.push({ start: seg.start, end: portStart });
                }
                if (seg.end > portEnd) {
                    nouveauxSegments.push({ start: portEnd, end: seg.end });
                }
            }
        });
        segmentsSol = nouveauxSegments;
    });

    segmentsSol.forEach(seg => {
        const len = seg.end - seg.start;
        if (len > 1) { // Ignore tiny scraps
            elements.push({
                type: 'rail',
                position: 'bas',
                x: seg.start,
                y: 0,
                longueur: len,
                orientation: 'horizontal'
            });
            stats.longueurTotaleRails += len;
            stats.railsBas++;
        }
    });

    // C1. Linteau au-dessus des portes (haut de l'huisserie)
    portes.forEach(porte => {
        elements.push({
            type: 'rail',
            position: 'linteau',
            x: porte.x,
            y: porte.y + porte.hauteur,
            longueur: porte.largeur,
            orientation: 'horizontal'
        });
        stats.longueurTotaleRails += porte.largeur;
    });

    // C2. Rails Intermédiaires (Linteaux/Appuis pour fenêtres)
    // Haut de fenêtre (Header) et Bas de fenêtre (Sill)
    const fenetres = ouvertures.filter(o => o.y > 0.1 && estStructurelle(o));
    fenetres.forEach(fen => {
        // Appui (Bas de fenêtre)
        elements.push({
            type: 'rail',
            position: 'appui',
            x: fen.x,
            y: fen.y,
            longueur: fen.largeur,
            orientation: 'horizontal'
        });
        stats.longueurTotaleRails += fen.largeur;

        // Linteau (Haut de fenêtre)
        elements.push({
            type: 'rail',
            position: 'linteau',
            x: fen.x,
            y: fen.y + fen.hauteur,
            longueur: fen.largeur,
            orientation: 'horizontal'
        });
        stats.longueurTotaleRails += fen.largeur;
    });


    // --- 2. Montants Verticaux (Studs) ---

    // Identify X positions
    const xPositions = new Set();

    // Regulier spacing
    for (let x = 0; x <= largeur + 0.1; x += ENTRAXE_STANDARD) {
        xPositions.add(Math.min(x, largeur)); // Clamp to end
    }
    // Force end if not already present (optimization: typically loop goes up to width)
    xPositions.add(largeur);

    // Framing Openings (structurelles uniquement : portes et fenêtres).
    // Les prises/interrupteurs/tuyaux n'ajoutent pas de montants d'encadrement.
    const ouverturesStructurelles = ouvertures.filter(estStructurelle);
    ouverturesStructurelles.forEach(o => {
        xPositions.add(o.x);
        xPositions.add(o.x + o.largeur);
    });

    // Sort valid X positions
    const sortedX = Array.from(xPositions).sort((a, b) => a - b);

    sortedX.forEach(x => {
        // For each vertical line, calculate segments (cut by openings)
        // Default: Full height
        let segments = [{ start: 0, end: hauteur }];

        // Intersect with openings at this X
        // An opening interrupts the rail if x is strictly inside (x > o.x AND x < o.x + w)
        // OR if it's a framing stud, it doesn't interrupt, it frames it. 
        // Wait, studs ARE the frame. So they go from floor to header, or sill to ceiling?
        // Actually, for a window:
        // - Studs at Left/Right edges run full height (usually doubled).
        // - Intermediate studs (spaced 60) are cut by the window (Allèe + Imposte).

        // Check if x is an "Edge" of an opening or "Inside" an opening
        const openingsAtThisX = ouverturesStructurelles.filter(o => {
            return (x > o.x && x < o.x + o.largeur); // Strictly inside (cut by window)
        });

        openingsAtThisX.forEach(o => {
            const oYBottom = o.y;
            const oYTop = o.y + o.hauteur;

            const nouveauxSegments = [];
            segments.forEach(seg => {
                // If segment overlaps with opening vertical range
                if (seg.end <= oYBottom || seg.start >= oYTop) {
                    nouveauxSegments.push(seg);
                } else {
                    // Cut it
                    // Part Below
                    if (seg.start < oYBottom) {
                        nouveauxSegments.push({ start: seg.start, end: oYBottom });
                    }
                    // Part Above
                    if (seg.end > oYTop) {
                        nouveauxSegments.push({ start: oYTop, end: seg.end });
                    }
                }
            });
            segments = nouveauxSegments;
        });

        segments.forEach(seg => {
            const len = seg.end - seg.start;
            if (len > 1) {
                elements.push({
                    type: 'montant',
                    x: x,
                    y: seg.start,
                    longueur: len,
                    orientation: 'vertical'
                });
                stats.longueurTotaleMontants += len;
                stats.montants++;
            }
        });
    });

    return {
        elements,
        stats
    };
};
